/**
 * Campos de entrada para uma condição individual de roteamento
 * 
 * Extraído de RuleConditionForm para permitir reutilização
 * no CompositeConditionBuilder (condições AND/OR)
 * 
 * @version 1.0.0
 * @date 2025-12-15
 */

import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RoutingConditionType, conditionTypeLabels } from '@/pages/tenant/RoutingRules';

// Interface para props do componente
export interface ConditionFieldsProps {
  /** Tipo de condição selecionado */
  conditionType: RoutingConditionType;
  /** Valor/configuração da condição */
  conditionValue: Record<string, unknown>;
  /** Callback quando tipo muda */
  onTypeChange: (type: RoutingConditionType) => void;
  /** Callback quando valor muda */
  onValueChange: (value: Record<string, unknown>) => void;
  /** Se deve mostrar o seletor de tipo (default: true) */
  showTypeSelector?: boolean;
}

// Dias da semana para time_range
const weekDays = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
];

/**
 * Renderiza campos de entrada para um tipo específico de condição
 */
export function ConditionFields({
  conditionType,
  conditionValue,
  onTypeChange,
  onValueChange,
  showTypeSelector = true,
}: ConditionFieldsProps) {
  const { currentTenant } = useAuth();
  const [keywordInput, setKeywordInput] = useState('');
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);

  // Carrega tags do tenant
  useEffect(() => {
    if (currentTenant?.id) {
      fetchTags();
    }
  }, [currentTenant?.id]);

  // Busca tags do tenant para condição contact_tag (via RPC server-side segura)
  const fetchTags = async () => {
    if (!currentTenant?.id) return;

    const { data } = await supabase.rpc('get_tenant_contact_tags', {
      p_tenant_hint: currentTenant.id,
    });

    setTags((data || []) as Array<{ id: string; name: string; color: string }>);
  };

  // Adicionar palavra-chave à lista
  const addKeyword = () => {
    if (!keywordInput.trim()) return;

    const keywords = (conditionValue.keywords as string[]) || [];
    if (!keywords.includes(keywordInput.trim().toLowerCase())) {
      onValueChange({
        ...conditionValue,
        keywords: [...keywords, keywordInput.trim().toLowerCase()],
      });
    }
    setKeywordInput('');
  };

  // Remover palavra-chave da lista
  const removeKeyword = (keyword: string) => {
    const keywords = (conditionValue.keywords as string[]) || [];
    onValueChange({
      ...conditionValue,
      keywords: keywords.filter(k => k !== keyword),
    });
  };

  // Toggle dia da semana
  const toggleDay = (day: number) => {
    const days = (conditionValue.days as number[]) || [1, 2, 3, 4, 5];
    if (days.includes(day)) {
      onValueChange({
        ...conditionValue,
        days: days.filter(d => d !== day),
      });
    } else {
      onValueChange({
        ...conditionValue,
        days: [...days, day].sort(),
      });
    }
  };

  // Toggle tag selecionada
  const toggleTag = (tagId: string) => {
    const tagIds = (conditionValue.tag_ids as string[]) || [];
    if (tagIds.includes(tagId)) {
      onValueChange({
        ...conditionValue,
        tag_ids: tagIds.filter(id => id !== tagId),
      });
    } else {
      onValueChange({
        ...conditionValue,
        tag_ids: [...tagIds, tagId],
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Seletor de tipo (opcional) */}
      {showTypeSelector && (
        <div className="space-y-2">
          <Label>Tipo de Condição</Label>
          <Select
            value={conditionType}
            onValueChange={(value) => {
              onTypeChange(value as RoutingConditionType);
              // Reset value quando muda tipo
              onValueChange({});
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(conditionTypeLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* ========== Campos específicos por tipo ========== */}
      
      {/* Condição: Palavra-chave */}
      {conditionType === 'keyword' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Palavras-chave</Label>
            <div className="flex gap-2">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="Digite uma palavra-chave..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              />
              <Button type="button" variant="outline" onClick={addKeyword}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Lista de palavras-chave */}
          <div className="flex flex-wrap gap-2">
            {((conditionValue.keywords as string[]) || []).map((keyword) => (
              <Badge key={keyword} variant="secondary" className="gap-1">
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(keyword)}
                  className="hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>

          {/* Tipo de match */}
          <div className="space-y-2">
            <Label>Tipo de correspondência</Label>
            <Select
              value={(conditionValue.match_type as string) || 'contains'}
              onValueChange={(value) => onValueChange({ ...conditionValue, match_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contains">Contém (parcial)</SelectItem>
                <SelectItem value="exact">Exata</SelectItem>
                <SelectItem value="starts_with">Começa com</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Condição: Expressão Regular */}
      {conditionType === 'regex' && (
        <div className="space-y-2">
          <Label>Padrão Regex</Label>
          <Input
            value={(conditionValue.pattern as string) || ''}
            onChange={(e) => onValueChange({ ...conditionValue, pattern: e.target.value })}
            placeholder="Ex: ^(oi|olá|bom dia)"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Use expressões regulares para padrões complexos. Case insensitive.
          </p>
        </div>
      )}

      {/* Condição: Primeira Mensagem */}
      {conditionType === 'first_message' && (
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            Esta condição é satisfeita quando é a primeira mensagem de um novo contato 
            (ou a primeira mensagem após a conversa ser fechada).
          </p>
        </div>
      )}

      {/* Condição: Tag do Contato */}
      {conditionType === 'contact_tag' && (
        <div className="space-y-3">
          <Label>Tags do Contato</Label>
          <p className="text-xs text-muted-foreground">
            A condição é satisfeita se o contato tiver pelo menos uma das tags selecionadas.
          </p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => {
              const isSelected = ((conditionValue.tag_ids as string[]) || []).includes(tag.id);
              return (
                <Badge
                  key={tag.id}
                  variant={isSelected ? 'default' : 'outline'}
                  className="cursor-pointer"
                  style={isSelected ? { backgroundColor: tag.color } : {}}
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.name}
                </Badge>
              );
            })}
          </div>
          {tags.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma tag cadastrada. Crie tags na página de Contatos.
            </p>
          )}
        </div>
      )}

      {/* Condição: Faixa de Horário */}
      {conditionType === 'time_range' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Horário Inicial</Label>
              <Input
                type="time"
                value={(conditionValue.start as string) || '08:00'}
                onChange={(e) => onValueChange({ ...conditionValue, start: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Horário Final</Label>
              <Input
                type="time"
                value={(conditionValue.end as string) || '18:00'}
                onChange={(e) => onValueChange({ ...conditionValue, end: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Dias da Semana</Label>
            <div className="flex flex-wrap gap-2">
              {weekDays.map((day) => {
                const isSelected = ((conditionValue.days as number[]) || [1, 2, 3, 4, 5]).includes(day.value);
                return (
                  <Button
                    key={day.value}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Condição: Execução Manual */}
      {conditionType === 'manual_execution' && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-600 dark:text-amber-400 text-lg">🖱️</span>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Execução Manual</p>
          </div>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Esta regra será executada apenas quando um agente clicar no botão 
            "Executar" durante uma conversa no chat. Não será disparada automaticamente.
          </p>
        </div>
      )}

      {/* Condição: Webhook Externo */}
      {conditionType === 'webhook' && (
        <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-rose-600 dark:text-rose-400 text-lg">🔗</span>
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300">Webhook Externo</p>
          </div>
          <p className="text-sm text-rose-600 dark:text-rose-400">
            Esta regra será executada quando um sistema externo (ClickSign, ERP, CRM, etc.) 
            enviar uma requisição para a URL do webhook. Configure o mapeamento de campos abaixo.
          </p>
        </div>
      )}
    </div>
  );
}
